"use client";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { PINNED_COLUMN_CLASS, TABLE_ROW_GROUP_CLASS } from "@/lib/table-styles";

export type CellTextOverflow = "wrap" | "ellipsis" | "clip" | "unset";

type AriaSort = "none" | "ascending" | "descending" | "other";

export type ColumnDefWithAriaSort<TData, TValue> = ColumnDef<TData, TValue> & {
  ariaSort?: AriaSort;
};

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  maxHeight?: string;
  enableVerticalScroll?: boolean;
  enableHorizontalScroll?: boolean;
  stickyHeader?: boolean;
  rowClassName?: string | ((row: TData) => string);
  cellTextOverflow?: CellTextOverflow;
  // Height utility applied to each data cell (e.g. "h-10" for 40px rows).
  // Overrides the default cell height via tailwind-merge. Opt-in so other
  // tables keep the standard density.
  rowHeightClassName?: string;
  // Height utility applied to each header cell (e.g. "h-8" for a 32px header).
  // Overrides the default header height via tailwind-merge. Opt-in.
  headerHeightClassName?: string;
  // Pins the last column against the right edge so it stays put while the other
  // columns scroll under it. For the per-row actions column — pair it with a
  // table that can actually overflow (see `.table-dense` in app.css).
  pinLastColumn?: boolean;
  // Rendered in place of the rows when `data` is empty, spanning every column so
  // the header row stays visible above it. This is how a no-results list keeps its
  // column headers while showing why it is empty; a pristine-empty list replaces
  // the whole table instead.
  emptyContent?: React.ReactNode;
}

const overflowClasses: Readonly<Record<CellTextOverflow, string>> = {
  wrap: "whitespace-normal break-words",
  ellipsis: "truncate whitespace-nowrap overflow-hidden",
  clip: "whitespace-nowrap overflow-clip",
  unset: "",
};

const TOOLTIP_CHAR_LIMIT: number = 20 as const;

function getCellTooltipContent(cellValue: unknown): string | undefined {
  if (typeof cellValue === "string" || typeof cellValue === "number") {
    return String(cellValue);
  }
  // For ReactNode or null/undefined - don't show a tooltip by default
  return undefined;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick = () => {},
  maxHeight = "400px",
  enableVerticalScroll = true,
  enableHorizontalScroll = true,
  stickyHeader = true,
  rowClassName,
  cellTextOverflow = "ellipsis",
  rowHeightClassName,
  headerHeightClassName,
  pinLastColumn = false,
  emptyContent,
}: DataTableProps<TData, TValue>): React.ReactElement {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    enableColumnResizing: true,
  });

  const cellOverflowClass: string = overflowClasses[cellTextOverflow];

  const isLastColumn = (index: number, total: number): boolean =>
    pinLastColumn && index === total - 1;

  // Helper: Render a table cell with optional tooltip if value is longer than TOOLTIP_CHAR_LIMIT
  const renderTableCell = (
    cell: ReturnType<
      ReturnType<typeof table.getRowModel>["rows"][number]["getVisibleCells"]
    >[number],
    cellIndex: number,
    cellCount: number
  ): React.ReactElement => {
    const pinned = isLastColumn(cellIndex, cellCount);
    const cellContext = cell.getContext();
    const cellNode = flexRender(cell.column.columnDef.cell, cellContext);

    let contentText: string | undefined;
    if (cellTextOverflow === "ellipsis") {
      // Try to extract the raw value for tooltip, otherwise fall back
      contentText = getCellTooltipContent(cellContext.getValue?.());
    }

    const showTooltip: boolean =
      cellTextOverflow === "ellipsis" &&
      typeof contentText === "string" &&
      contentText.trim() !== "" &&
      contentText.trim().length > TOOLTIP_CHAR_LIMIT;

    if (showTooltip) {
      // Show tooltip only when content length exceeds limit.
      // The key must live on the element returned to the .map() callback
      // (this TooltipProvider), not on the inner TableCell.
      return (
        <TooltipProvider key={cell.id} delayDuration={400}>
          <Tooltip>
            <TooltipTrigger asChild>
              <TableCell
                style={{ width: cell.column.getSize() }}
                className={cn(
                  cellOverflowClass,
                  rowHeightClassName,
                  pinned && PINNED_COLUMN_CLASS
                )}
              >
                {cellNode}
              </TableCell>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs break-words">
              {contentText}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return (
      <TableCell
        key={cell.id}
        style={{ width: cell.column.getSize() }}
        className={cn(
          cellOverflowClass,
          rowHeightClassName,
          pinned && PINNED_COLUMN_CLASS
        )}
      >
        {cellNode}
      </TableCell>
    );
  };

  return (
    <Table>
      <TableHeader
        className="bg-muted"
        style={
          stickyHeader
            ? {
                position: "sticky",
                top: 0,
                zIndex: 10,
              }
            : undefined
        }
      >
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header, headerIndex) => {
              // One evaluation per header: the flag drives both the inline
              // position and the pinned classes below.
              const pinned: boolean = isLastColumn(
                headerIndex,
                headerGroup.headers.length
              );
              // Check if column has custom aria-sort property
              const columnDef = header.column
                .columnDef as ColumnDefWithAriaSort<TData, TValue>;
              const ariaSort = columnDef.ariaSort;

              return (
                <TableHead
                  key={header.id}
                  style={{
                    width: header.getSize(),
                    // `relative` anchors the resize handle; a pinned column needs
                    // `sticky` instead, and a higher stack than the sticky thead.
                    position: pinned ? "sticky" : "relative",
                  }}
                  className={cn(
                    "group bg-muted truncate",
                    headerHeightClassName,
                    pinned && cn(PINNED_COLUMN_CLASS, "z-20")
                  )}
                  aria-sort={ariaSort}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  {header.column.getCanResize() &&
                    (() => {
                      const columnSizing = table.getState().columnSizing;
                      const columnId = header.column.id;
                      const currentSize =
                        columnSizing[columnId] || header.column.getSize();
                      const minSize = header.column.columnDef.minSize || 50;
                      const maxSize = header.column.columnDef.maxSize || 1000;

                      const handleKeyDown = (e: React.KeyboardEvent) => {
                        e.preventDefault();
                        const step = e.shiftKey ? 50 : 10; // Larger step with Shift key
                        let newSize = currentSize;

                        switch (e.key) {
                          case "ArrowLeft":
                            newSize = Math.max(minSize, currentSize - step);
                            break;
                          case "ArrowRight":
                            newSize = Math.min(maxSize, currentSize + step);
                            break;
                          case "Home":
                            newSize = minSize;
                            break;
                          case "End":
                            newSize = maxSize;
                            break;
                          case "PageUp":
                            newSize = Math.max(minSize, currentSize - 50);
                            break;
                          case "PageDown":
                            newSize = Math.min(maxSize, currentSize + 50);
                            break;
                          case "Enter":
                          case " ":
                            // For Enter/Space, we could start a resize mode, but for now just prevent default
                            return;
                          default:
                            return;
                        }

                        table.setColumnSizing((prev) => ({
                          ...prev,
                          [columnId]: newSize,
                        }));
                      };

                      return (
                        <div
                          role="separator"
                          aria-orientation="horizontal"
                          aria-valuenow={currentSize}
                          aria-valuemin={minSize}
                          aria-valuemax={maxSize}
                          tabIndex={0}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          onKeyDown={handleKeyDown}
                          /*
                            The opacity classes were toggling the visibility of an
                            element that had no background to show: `opacity-0` →
                            `opacity-100` on an empty 4px div is invisible at both
                            ends. So every column boundary was a focusable,
                            keyboard-resizable control that never drew itself — you
                            could tab into it and never find out where you were.

                            `bg-border` gives it something to reveal, and the
                            stronger colour on hover and focus says which boundary
                            is about to move.
                          */
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize rounded-full bg-border select-none opacity-0 transition-opacity group-hover:opacity-100 hover:bg-primary hover:opacity-100 focus-visible:bg-primary focus-visible:opacity-100 focus-visible:outline-none"
                          style={{ transform: "translateX(50%)" }}
                        />
                      );
                    })()}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody
        className={`${
          enableHorizontalScroll ? "overflow-x-auto" : ""
        } ${enableVerticalScroll ? "overflow-y-auto" : ""}`}
        style={{
          maxHeight: enableVerticalScroll ? maxHeight : undefined,
        }}
      >
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => {
            const customRowClassName =
              typeof rowClassName === "function"
                ? rowClassName(row.original)
                : (rowClassName ?? "");
            // The selected background comes from TableRow's own
            // data-[state=selected] variant — adding a second bg class here
            // would leave the rendered colour to stylesheet order.
            const combinedClass = [TABLE_ROW_GROUP_CLASS, customRowClassName]
              .filter(Boolean)
              .join(" ");
            return (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                onClick={() => onRowClick(row.original)}
                className={combinedClass}
              >
                {row
                  .getVisibleCells()
                  .map((cell, cellIndex, cells) =>
                    renderTableCell(cell, cellIndex, cells.length)
                  )}
              </TableRow>
            );
          })
        ) : (
          <TableRow>
            <TableCell
              colSpan={table.getVisibleLeafColumns().length}
              className={cn(emptyContent ? "py-16" : "h-24 text-center")}
            >
              {emptyContent ?? "No results."}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
